// src/components/Header.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaPhone, FaEnvelope, FaClock, FaBars, FaTimes, FaUser, FaAward } from "react-icons/fa";
import logo from "../assets/images/favicon.ico";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [profileExists, setProfileExists] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const scrollPosition = useRef(0);
  const skipRestoreScroll = useRef(false); // 👈 new flag

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    checkProfileExists();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [mobileMenuOpen]);

  // Prevent body scrolling when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      scrollPosition.current = window.scrollY;
      document.body.classList.add("menu-open");
      document.body.style.top = `-${scrollPosition.current}px`;
    } else {
      document.body.classList.remove("menu-open");
      document.body.style.top = "";
      // 👇 only restore scroll if not navigating via anchor
      if (!skipRestoreScroll.current) {
        window.scrollTo(0, scrollPosition.current);
      }
      skipRestoreScroll.current = false; // reset
    }
  }, [mobileMenuOpen]);

  const checkProfileExists = async () => {
    try {
      const response = await fetch("https://alafoogarage.com/ahalafoo/api/profile");
      setProfileExists(response.ok);
    } catch (error) {
      setProfileExists(false);
    }
  };

  const openProfile = () => {
    window.open("https://alafoogarage.com/ahalafoo/api/profile", "_blank", "noopener,noreferrer");
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  // Close menu when clicking on nav links
  const handleNavClick = (e) => {
    if (isMobile) {
      const href = e.target.getAttribute("href");
      if (href && href.startsWith("#")) {
        // 👇 allow browser to scroll naturally
        skipRestoreScroll.current = true;
      }
      closeMobileMenu();
    }
  };

  const menuItems = [
    { id: 1, name: "Home", href: "#home", icon: "🏠" },
    { id: 2, name: "Departments", href: "#departments", icon: "🏢" },
    { id: 3, name: "Projects", href: "#projects", icon: "📊" },
    { id: 4, name: "Leadership", href: "#leadership", icon: "👥" },
    { id: 5, name: "Certifications", href: "#certificates", icon: "🏆" },
    { id: 6, name: "Contact", href: "#contact", icon: "📞" },
  ];

  return (
    <header className={`header ${scrolled ? "scrolled" : ""}`}>
      {/* Top Info Bar - Hidden on mobile */}
      <div className="header-top">
        <div className="container">
          <div className="contact-info">
            <span>
              <FaPhone /> +973 17874756
            </span>
            <span>
              <FaEnvelope /> alafoo@batelco.com.bh
            </span>
            <span>
              <FaClock /> 7:00AM to 4:00PM (Friday Closed)
            </span>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="header-main">
        <div className="container" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Logo + Company Name */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="logo"
          >
            <div className="logo-wrapper" style={{ display: "flex", alignItems: "center", gap: "15px" }}>
              <img
                src={logo}
                alt="Company Logo"
                className="company-logo"
                style={{ width: "60px", height: "60px" }}
              />
              <div>
                <h1 className="company-name">ABDULHADI AL-AFOO W.L.L</h1>
                <p className="company-tagline">Building Bahrain's Infrastructure Since 1961</p>
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation Menu */}
          <nav className="nav-menu desktop-menu">
            <ul>
              {menuItems.map(item => (
                <li key={item.id}>
                  <a href={item.href}>{item.name}</a>
                </li>
              ))}
              {profileExists && (
                <li>
                  <a href="#" onClick={openProfile} className="profile-link">
                    <FaUser />
                  </a>
                </li>
              )}
            </ul>
          </nav>

          {/* Mobile Menu Button */}
          {isMobile && (
            <button className="mobile-menu-btn" onClick={toggleMobileMenu} aria-label="Toggle menu">
              {mobileMenuOpen ? <FaTimes /> : <FaBars />}
            </button>
          )}

          {/* Mobile Menu Popup */}
          <AnimatePresence>
            {isMobile && mobileMenuOpen && (
              <>
                <motion.div
                  className="mobile-menu-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  onClick={closeMobileMenu}
                />

                <motion.div
                  className="mobile-menu-popup"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ type: "spring", damping: 25, stiffness: 300 }}
                >
                  <div className="popup-header">
                    <span className="popup-title">Menu</span>
                  </div>

                  <div className="popup-content">
                    <ul>
                      {menuItems.map(item => (
                        <motion.li
                          key={item.id}
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                          <a href={item.href} onClick={handleNavClick}>
                            <span className="menu-icon">{item.icon}</span>
                            {item.name}
                          </a>
                        </motion.li>
                      ))}

                      {profileExists && (
                        <motion.li
                          whileHover={{ x: 5 }}
                          transition={{ type: "spring", stiffness: 400, damping: 17 }}
                        >
                          <a href="#" onClick={openProfile} className="profile-mobile-link">
                            <span className="menu-icon">👤</span>
                          </a>
                        </motion.li>
                      )}
                    </ul>

                    <div className="popup-footer">
                      <div className="company-info-mobile">
                        <p>ABDULHADI AL-AFOO W.L.L</p>
                        <p>Building Bahrain's Infrastructure Since 1961</p>
                      </div>
                      <button className="popup-close-bottom" onClick={closeMobileMenu}>
                        Close Menu
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </header>
  );
};

export default Header;